/**
 * Orchestrates one Send (spec section 4): save with an Idempotency-Key, PUT each image to its
 * presigned URL, confirm, toast. Owned by `FeedbackHost`, so a Send continues while minimized.
 *
 * Double-submit safety (SC-2) is two layers: a synchronous in-flight ref set before the first await
 * (a disabled button alone cannot stop a same-tick double click) and the Idempotency-Key. A retry after
 * a failed save with an unchanged draft reuses the first attempt's already-built body and key, so it
 * is byte-identical (ADR-005 would answer 400 IDEMPOTENCY_KEY_MISMATCH if the body drifted, and the
 * console and API logs in the context change between attempts). Any user-controlled change mints a new key.
 */
import { useCallback, useRef } from 'react';
import { buildClientContext } from '@/lib/feedback';
import { useNotifications } from '@/hooks/useNotifications';
import { useIdempotencyKey } from '@/tools/shared/hooks/useIdempotencyKey';
import { confirmAttachments, putImage, saveFeedback } from './feedbackApi';
import {
  announce,
  clearDraft,
  closePanel,
  getFeedbackState,
  requestFocus,
  setProgress,
  setSaveError,
  setSending,
  setUnavailable,
  type SaveError,
} from './feedbackStore';
import { toSaveCapture, toSaveContext } from './toSaveContext';
import { useClientContextInput } from './useClientContextInput';
import { useInvalidateFeedbackAvailability } from './useFeedbackAvailability';
import type { DraftImage, SaveRequest, SaveResponse } from './types';

export type SendOutcome = 'sent' | 'invalid' | 'skipped' | 'failed';

const UPLOAD_CONCURRENCY = 3;

interface Attempt {
  signature: string;
  body: SaveRequest;
}

interface ErrorShape {
  response?: { status?: number };
  serverError?: unknown;
}

/** The server's 400 reason: a bare string, or `{ message }`, or a generic fallback. */
function serverMessage(server: unknown): string {
  if (typeof server === 'string') return server;
  const message = (server as { message?: unknown } | undefined)?.message;
  return typeof message === 'string' ? message : 'The request was not valid.';
}

function classifySaveError(error: unknown): { kind: 'unavailable' } | { kind: 'error'; error: SaveError } {
  const e = (error ?? {}) as ErrorShape;
  const status = e.response?.status;
  if (status === 404) return { kind: 'unavailable' };
  if (status === 403) {
    return {
      kind: 'error',
      error: { kind: 'forbidden', description: 'You no longer have permission to send feedback.' },
    };
  }
  if (status === 429) {
    return {
      kind: 'error',
      error: {
        kind: 'rate-limit',
        description: "Your draft is kept. You're sending too fast; wait a moment and try again.",
      },
    };
  }
  if (status === 400) {
    const message = serverMessage(e.serverError);
    return {
      kind: 'error',
      error: { kind: 'validation', description: `Your draft is kept. The server rejected it: ${message}` },
    };
  }
  return {
    kind: 'error',
    error: { kind: 'network', description: 'Your draft is kept. Check your connection and try again.' },
  };
}

function signatureOf(images: DraftImage[]): string {
  const s = getFeedbackState();
  return JSON.stringify({
    note: s.note,
    category: s.category,
    severity: s.severity,
    capture: s.capture,
    images: images.map((i) => [i.contentType, i.bytes]),
  });
}

/** Runs `worker` over every index with a small fixed concurrency; each outcome is independent. */
async function runPool(count: number, worker: (index: number) => Promise<void>): Promise<void> {
  let next = 0;
  const lane = async (): Promise<void> => {
    while (next < count) {
      const index = next;
      next += 1;
      await worker(index);
    }
  };
  await Promise.all(Array.from({ length: Math.min(UPLOAD_CONCURRENCY, count) }, lane));
}

type FeedbackState = ReturnType<typeof getFeedbackState>;
type ContextSource = ReturnType<typeof useClientContextInput>;
type Notify = ReturnType<typeof useNotifications>['addNotification'];

/** The save body for the current draft. Built once per signature so a retry is byte-identical. */
function buildSaveBody(s: FeedbackState, images: DraftImage[], source: ContextSource, machineId: string | null): SaveRequest {
  const capture = toSaveCapture(s.capture);
  const body: SaveRequest = {
    note: s.note,
    category: s.category,
    severity: s.severity,
    captureMode: capture?.mode ?? 'page',
    context: { client: toSaveContext(buildClientContext(source.build())) },
    attachments: images.map((i) => ({ contentType: i.contentType, bytes: i.bytes })),
  };
  if (capture) body.capture = capture;
  if (source.activeSiteId) body.reportedSiteId = source.activeSiteId;
  if (machineId) body.machineId = machineId;
  return body;
}

/** Reflects a failed save in the store: unavailable (404) hands focus to Copy note; anything else keeps the draft. */
function applySaveFailure(error: unknown, invalidateAvailability: () => void): void {
  const result = classifySaveError(error);
  if (result.kind === 'unavailable') {
    setUnavailable(true);
    invalidateAvailability();
    requestFocus('feedback-copy-note');
  } else {
    setSaveError(result.error);
  }
  setSending(false);
}

/** PUTs every image to its presigned URL; `true` at an index means that image uploaded. */
async function uploadImages(images: DraftImage[], saved: SaveResponse, step: () => void): Promise<boolean[]> {
  const uploaded: boolean[] = images.map(() => false);
  await runPool(images.length, async (index) => {
    const target = saved.uploads[index];
    const image = images[index];
    if (target && image?.blob) uploaded[index] = await putImage(target.url, target.headers, image.blob);
    step();
  });
  return uploaded;
}

/** Confirms the uploaded attachments; a confirm failure is reported, never thrown. */
async function confirmUploads(
  saved: SaveResponse,
  uploaded: boolean[],
): Promise<{ confirmedCount: number; confirmFailed: boolean }> {
  const attachmentIds = saved.uploads.filter((_, index) => uploaded[index]).map((u) => u.attachmentId);
  if (attachmentIds.length === 0) return { confirmedCount: 0, confirmFailed: false };
  try {
    const confirmed = await confirmAttachments({ feedbackId: saved.feedbackId, attachmentIds });
    return {
      confirmedCount: confirmed.attachments.filter((a) => a.uploadStatus === 'uploaded').length,
      confirmFailed: false,
    };
  } catch {
    return { confirmedCount: 0, confirmFailed: true };
  }
}

/** The Sent toast: success when every image landed, otherwise a sticky warning saying what did not. */
function notifySent(
  addNotification: Notify,
  feedbackId: string,
  imageCount: number,
  { confirmedCount, confirmFailed }: { confirmedCount: number; confirmFailed: boolean },
): void {
  const failedCount = imageCount - confirmedCount;
  const title = `Sent · ${feedbackId}`;
  if (imageCount === 0 || (!confirmFailed && failedCount === 0)) {
    addNotification({ type: 'success', title, duration: 8000 });
    return;
  }
  addNotification({
    type: 'warning',
    title,
    message: confirmFailed
      ? 'Images were uploaded but could not be confirmed. They may show as not uploaded.'
      : `${failedCount} of ${imageCount} images did not upload. The note was saved.`,
    duration: 0,
  });
}

export function useSendFeedback(): { send: () => Promise<SendOutcome> } {
  const { addNotification } = useNotifications();
  const idempotency = useIdempotencyKey();
  const source = useClientContextInput();
  const invalidateAvailability = useInvalidateFeedbackAvailability();
  const inFlight = useRef(false);
  const lastAttempt = useRef<Attempt | null>(null);

  const send = useCallback(async (): Promise<SendOutcome> => {
    if (inFlight.current) return 'skipped';
    inFlight.current = true;
    try {
      const s = getFeedbackState();
      if (s.note.trim().length === 0) return 'invalid';
      const images = s.images;
      if (images.some((i) => i.blob === null)) {
        announce('Wait for screenshots to finish processing.');
        return 'skipped';
      }

      setSending(true, 0);
      setSaveError(null);
      announce('Sending feedback…');
      const total = 2 + images.length;
      let done = 0;
      const step = (): void => {
        done += 1;
        setProgress(Math.round((100 * done) / total));
      };

      const signature = signatureOf(images);
      let attempt = lastAttempt.current;
      if (attempt?.signature !== signature) {
        idempotency.regenerate();
        attempt = { signature, body: buildSaveBody(s, images, source, await source.resolveMachineId()) };
        lastAttempt.current = attempt;
      }

      let saved: SaveResponse;
      try {
        saved = await saveFeedback(attempt.body, idempotency.current());
      } catch (error) {
        applySaveFailure(error, invalidateAvailability);
        return 'failed';
      }
      step();

      const uploaded = await uploadImages(images, saved, step);
      const confirmation = await confirmUploads(saved, uploaded);
      setProgress(100);
      notifySent(addNotification, saved.feedbackId, images.length, confirmation);

      // The record exists, so the draft is done either way.
      idempotency.reset();
      lastAttempt.current = null;
      clearDraft();
      closePanel();
      return 'sent';
    } finally {
      inFlight.current = false;
    }
  }, [addNotification, idempotency, source, invalidateAvailability]);

  return { send };
}
